// src/components/AIAgent/EscalationReview.jsx

import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAIAgentStore } from "../../services/useAIAgentStore";
import ConversationDetail from "./ConversationDetail";
import "./AIAgent.css";

const EscalationReview = () => {
  const {
    pendingEscalations,
    loading,
    error,
    fetchPendingEscalations,
    addConversationFeedback,
    markConversationReviewed,
    fetchConversation,
    currentConversation,
    clearError,
  } = useAIAgentStore();

  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackConversationId, setFeedbackConversationId] = useState(null);
  const [feedbackRating, setFeedbackRating] = useState(3);
  const [feedbackNotes, setFeedbackNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPendingEscalations();
  }, []);

  const handleViewConversation = (conversationId) => {
    setSelectedConversationId(conversationId);
  };

  const handleOpenFeedback = (conversationId) => {
    setFeedbackConversationId(conversationId);
    setFeedbackRating(3);
    setFeedbackNotes("");
    setShowFeedbackModal(true);
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackConversationId) return;

    setSubmitting(true);
    try {
      await addConversationFeedback(feedbackConversationId, feedbackRating, feedbackNotes || null);
      setShowFeedbackModal(false);
      setFeedbackConversationId(null);
    } catch (err) {
      console.error("Error submitting feedback:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkReviewed = async (conversationId) => {
    try {
      await markConversationReviewed(conversationId);
      toast.success("Lead reativado! O agente IA voltara a responder as mensagens.");
    } catch (err) {
      console.error("Error marking reviewed:", err);
      toast.error("Erro ao reativar o lead. Tente novamente.");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // If viewing a conversation detail
  if (selectedConversationId) {
    return (
      <ConversationDetail
        conversationId={selectedConversationId}
        onClose={() => setSelectedConversationId(null)}
      />
    );
  }

  return (
    <div className="escalation-review">
      <div className="escalation-header">
        <div>
          <h3>Escalacoes Pendentes</h3>
          <p style={{ margin: "4px 0 0", fontSize: "0.875rem", color: "var(--text-secondary)" }}>
            Clique em "Reativar Lead" para o agente IA voltar a responder
          </p>
        </div>
        <button className="btn-refresh" onClick={fetchPendingEscalations} disabled={loading}>
          Atualizar
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading && pendingEscalations.length === 0 ? (
        <div className="loading-state">Carregando escalacoes...</div>
      ) : pendingEscalations.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">V</div>
          <p>Nenhuma escalacao pendente de review</p>
          <span>Todas as conversas escaladas foram revisadas.</span>
        </div>
      ) : (
        <div className="escalations-list">
          {pendingEscalations.map((conv) => (
            <div key={conv.id} className="escalation-card">
              <div className="escalation-info">
                <div className="escalation-main">
                  <span className="escalation-id">#{conv.id?.slice(0, 8)}</span>
                  <span className="escalation-lead">Lead: {conv.lead_id?.slice(0, 8)}</span>
                </div>
                <div className="escalation-meta">
                  <span>Escalada em: {formatDate(conv.escalated_at)}</span>
                  <span>{conv.message_count} mensagens</span>
                </div>
                {conv.escalation_reason && (
                  <div className="escalation-reason">
                    <strong>Motivo:</strong> {conv.escalation_reason}
                  </div>
                )}
                {conv.feedback && (
                  <div className="escalation-feedback-preview">
                    <strong>Feedback:</strong> {conv.feedback.rating}/5
                    {conv.feedback.notes && ` - ${conv.feedback.notes.slice(0, 50)}...`}
                  </div>
                )}
              </div>

              <div className="escalation-actions">
                <button
                  className="btn-view"
                  onClick={() => handleViewConversation(conv.id)}
                >
                  Ver Conversa
                </button>
                {!conv.feedback && (
                  <button
                    className="btn-feedback"
                    onClick={() => handleOpenFeedback(conv.id)}
                  >
                    Dar Feedback
                  </button>
                )}
                <button
                  className="btn-reviewed"
                  onClick={() => handleMarkReviewed(conv.id)}
                  title="Marca como revisado e reativa o lead para o agente IA voltar a responder"
                >
                  Reativar Lead
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="modal-overlay" onClick={() => setShowFeedbackModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Feedback da Escalacao</h3>
              <button className="btn-close" onClick={() => setShowFeedbackModal(false)}>
                X
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Avaliacao do Atendimento IA</label>
                <div className="rating-input">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      className={`rating-btn ${feedbackRating === value ? "active" : ""}`}
                      onClick={() => setFeedbackRating(value)}
                    >
                      {value}
                    </button>
                  ))}
                </div>
                <div className="rating-labels">
                  <span>Ruim</span>
                  <span>Excelente</span>
                </div>
              </div>

              <div className="form-group">
                <label>Notas / Observacoes</label>
                <textarea
                  value={feedbackNotes}
                  onChange={(e) => setFeedbackNotes(e.target.value)}
                  placeholder="Descreva o que poderia ser melhorado, se a escalacao foi necessaria, etc..."
                  rows={4}
                />
              </div>

              <div className="feedback-tips">
                <strong>Dicas para feedback:</strong>
                <ul>
                  <li>A IA deveria ter respondido de forma diferente?</li>
                  <li>A escalacao foi necessaria ou poderia ter sido evitada?</li>
                  <li>Qual informacao estava faltando para a IA?</li>
                </ul>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowFeedbackModal(false)}>
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={handleSubmitFeedback}
                disabled={submitting}
              >
                {submitting ? "Enviando..." : "Enviar Feedback"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EscalationReview;
